import React, { useState, useEffect } from "react";
import { graphql, navigate } from "gatsby";
import Layout from "../../components/layout/layout";
import PostCardList from "../../components/postCardList/postCardList";
import {
  CARD_ORIENTATION,
  IMG_POSITION,
} from "../../components/common/post-card/postCard.constant";
import "./taggedPosts.scss";
import BreadcrumbHeader from "../../components/common/breadcrumb-header/breadcrumbHeader";
import { getDescription } from "./taggedPostsHelper";
import Seo from "../../components/seo";
import { SEO_META_NO_INDEX } from "../../constants/home";
import Pagination from "../../components/common/pagination/pagination";
import { IMG_VARIANTS } from "../../constants/imgVariants";
import {
  triggerBuzzClickEvent,
  triggerBuzzPageLoadEvent,
} from "../../utils/garfieldHelpers";
import {
  EVENT_NAMES,
  EVENT_SOURCE,
  SECTION_IDS,
} from "../../constants/garfieldEvents";
import {
  BUZZ_TAGGED_ARTICLES_LIST_FLOW,
  BUZZ_TAGGED_ARTICLES_LIST_PAGINATION_FLOW,
  TAG_ARTICLE_LIST_DATA,
  TAG_PAGE_DATA,
} from "./taggedPosts.constant";

const TaggedPosts = ({ data, pageContext }) => {
  const posts = data.allWpPost.nodes;
  const postCount = posts?.length || 0;
  const {
    pageNumber: currentPageNumber,
    totalPages,
    postsPerPage,
  } = pageContext;
  const [pageNumber, setPageNumber] = useState(
    currentPageNumber - 1
  );

  const breadcrumbLinks = [
    {
      uri: "/",
      name: "Buzz",
    },
    {
      uri: "",
      name: data?.wpTag?.name,
    },
  ];

  const handleUpdatePageNumber = pageNumber => {
    setPageNumber(pageNumber);
    if (pageNumber === 0) {
      navigate(data?.wpTag?.uri);
    } else {
      navigate(`${data?.wpTag?.uri}${pageNumber + 1}`);
    }
    triggerBuzzClickEvent(
      EVENT_NAMES.TAGGED_LIST_PAGINATION_CLICKED,
      {
        [TAG_PAGE_DATA.NAME]: data?.wpTag?.name,
        [TAG_PAGE_DATA.URL]: data?.wpTag?.uri,
        [TAG_PAGE_DATA.PAGE_NUMBER]: pageNumber,
      },
      SECTION_IDS.PAGINATION,
      EVENT_SOURCE.TAG_ARTICLE_LIST,
      BUZZ_TAGGED_ARTICLES_LIST_PAGINATION_FLOW
    );
  };

  useEffect(() => {
    triggerBuzzPageLoadEvent(
      EVENT_NAMES.TAG_LIST_PAGE_LOADED,
      {
        [TAG_ARTICLE_LIST_DATA.TAG_NAME]: data?.wpTag?.name,
        [TAG_ARTICLE_LIST_DATA.TAG_URI]: data?.wpTag.uri,
        [TAG_ARTICLE_LIST_DATA.PAGE_NUMBER]: pageNumber,
      }
    );
  }, []);

  return (
    <>
      <Layout
        articleBanner={
          <BreadcrumbHeader
            breadcrumbLinks={breadcrumbLinks}
            title={data?.wpTag?.name}
          />
        }
      >
        <Seo
          title={data?.wpTag?.name}
          description={data?.wpTag?.name}
          pageURI={data?.wpTag?.uri}
          meta={SEO_META_NO_INDEX}
        />
        {postCount && (
          <p className="description">
            {getDescription(postCount, data?.wpTag?.name)}
          </p>
        )}
        <PostCardList
          posts={posts}
          showMoreVisibility={false}
          cardOrientation={CARD_ORIENTATION.HORIZONTAL}
          imgPosition={IMG_POSITION.RIGHT}
          customClassName={"horizontal-list"}
          imgVariant={IMG_VARIANTS.ALL_ARTICLES}
          sectionId={SECTION_IDS.TAGGED_CATEGORY_LIST}
          source={EVENT_SOURCE.TAG_ARTICLE_LIST}
          flow={BUZZ_TAGGED_ARTICLES_LIST_FLOW}
        />
        {postCount > postsPerPage && (
          <Pagination
            pageNumber={pageNumber}
            totalPages={totalPages}
            updatePageNumber={handleUpdatePageNumber}
          />
        )}
      </Layout>
    </>
  );
};

export const pageQuery = graphql`
  query WPIndividualTagsPosts(
    $id: String!
    $offset: Int!
    $postsPerPage: Int!
  ) {
    allWpPost(
      filter: {
        tags: { nodes: { elemMatch: { id: { eq: $id } } } }
      }
      sort: { fields: [date], order: DESC }
      limit: $postsPerPage
      skip: $offset
    ) {
      nodes {
        excerpt
        uri
        date(formatString: "MMMM DD, YYYY")
        title
        excerpt
        slug
        author {
          node {
            id
            name
          }
        }
        seo {
          readingTime
        }
        categories {
          nodes {
            id
            name
            uri
            slug
          }
        }
        featuredImage {
          node {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid_tracedSVG
                }
              }
            }
          }
        }
        tags {
          nodes {
            id
            name
            uri
          }
        }
      }
    }
    categoriesData: allWpCategory {
      nodes {
        id
        name
        count
        uri
      }
    }
    recentPosts: allWpPost(
      sort: { order: DESC, fields: date }
      limit: 5
    ) {
      nodes {
        id
        title
        uri
      }
    }
    wpTag(id: { eq: $id }) {
      name
      uri
    }
  }
`;

export default TaggedPosts;
